import { Box, Flex, FlexProps, Heading, Link, SimpleGrid, Text } from '@chakra-ui/react';

import { ContentContainer } from '~/components/container';
import { SectionHeader } from '~/components/section-header';
import { COPY, STAT_ITEMS, STAT_SOURCES } from '~/features/ai-marketer/constants';
import { ClockIcon } from '~/features/experiments/ab_plp_redesign_2025/icons';

export function AiMarketerStatsSection() {
  return (
    <Box as='section' bg='grey.50' py={{ base: '48px', md: '96px' }}>
      <ContentContainer contentMaxWidth='content-max-width-md'>
        <Flex flexDirection='column' gap={{ base: '32px', md: '48px' }}>
          <SectionHeader
            color='gray.700'
            iconSlot={<ClockIcon color='gray.700' height='18px' width='18px' />}
            subtitle={COPY.stats.subtitle}
            titleSlot={
              <Heading
                as='h2'
                color='grey.900'
                letterSpacing='-0.02em'
                size={{ base: 'mobile-heading-2', md: 'heading-2' }}
                textAlign='center'
              >
                {COPY.stats.heading}
              </Heading>
            }
          />
          <Flex flexDirection='column' gap={{ base: '8px', md: '32px' }}>
            <SimpleGrid as='ul' columns={{ base: 1, md: 3 }} listStyleType='none' spacing={{ base: '8px', md: '24px' }}>
              {STAT_ITEMS.map((item) => (
                <Flex
                  key={item.value}
                  as='li'
                  bg='white'
                  border='1px solid'
                  borderColor='#F2F2F2'
                  borderRadius={{ base: '6px', md: '8px' }}
                  flexDirection='column'
                  gap='10px'
                  p={{ base: '20px', md: '32px' }}
                >
                  <Flex
                    borderLeft='2px solid'
                    flexDirection='column'
                    gap='16px'
                    pl='16px'
                    sx={{
                      borderImage: `linear-gradient(to bottom, rgba(${item.dividerRgb}, 1), rgba(${item.dividerRgb}, 0)) 1`,
                    }}
                  >
                    <Flex alignItems='flex-end' gap='10px' justifyContent='space-between'>
                      <Text
                        as='span'
                        color={item.color}
                        fontFamily='heading'
                        fontSize={{ base: '3rem', md: '3.375rem' }}
                        fontWeight='600'
                        letterSpacing={'suffix' in item ? '-0.03em' : '-0.08em'}
                        lineHeight={{ base: '2.5rem', md: '2.75rem' }}
                      >
                        {item.value}
                        {'suffix' in item && (
                          <Text as='span' fontSize={{ base: '1.875rem', md: '2rem' }} letterSpacing={0} ml='0.1em'>
                            {item.suffix}
                          </Text>
                        )}
                      </Text>
                      <SourceMarker display={{ base: 'inline-flex', md: 'none' }} mb='4px' source={item.source} />
                    </Flex>
                    <Text color='grey.900' fontSize={{ base: '0.75rem', md: '0.8125rem' }} lineHeight='140%'>
                      {item.description}
                      <SourceMarker
                        display={{ base: 'none', md: 'inline-flex' }}
                        ml='6px'
                        source={item.source}
                        verticalAlign='middle'
                      />
                    </Text>
                  </Flex>
                </Flex>
              ))}
            </SimpleGrid>
            <Flex
              columnGap={{ base: '24px', md: '32px' }}
              flexWrap='wrap'
              justifyContent={{ base: 'flex-start', md: 'center' }}
              pt={{ base: '8px', md: 0 }}
              px={{ base: '20px', md: 0 }}
              rowGap='8px'
            >
              {STAT_SOURCES.map((source) => (
                <Flex key={source.source} alignItems='center' gap='6px'>
                  <SourceMarker source={source.source} />
                  <Link
                    _visited={{ color: 'grey.500' }}
                    color='grey.500'
                    fontSize='0.625rem'
                    fontWeight='400'
                    href={source.href}
                    lineHeight='0.875rem'
                    textDecoration='underline'
                    isExternal
                  >
                    {source.label}
                  </Link>
                </Flex>
              ))}
            </Flex>
          </Flex>
        </Flex>
      </ContentContainer>
    </Box>
  );
}

function SourceMarker({ source, display = 'inline-flex', ...flexProps }: { source: number } & FlexProps) {
  return (
    <Flex
      alignItems='center'
      as='sup'
      border='1px solid'
      borderColor='grey.900'
      borderRadius='full'
      color='grey.900'
      display={display}
      lineHeight='1'
      top={0}
      {...flexProps}
      flexShrink={0}
      fontSize='7px'
      fontWeight='700'
      h='12px'
      justifyContent='center'
      w='12px'
    >
      {source}
    </Flex>
  );
}
